import { ButtonDesigns, ButtonTypes } from '@paywithextend/components'
import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Route, Switch, useHistory } from 'react-router'
import ApproverExpenseReports from 'src/app/ApproverExpenseReports/ApproverExpenseReports'
import BudgetsAllBudgets from 'src/app/BudgetsAllBudgets/BudgetsAllBudgets'
import BudgetsRequests from 'src/app/BudgetsRequests/BudgetsRequests'
import ExpensesTransactions from 'src/app/ExpensesTransactions/ExpensesTransactions'
import MyWalletVirtualCards from 'src/app/MyWallet/MyWalletVirtualCards'
import PhysicalCardsTable from 'src/components/DataGrid/tables/PhysicalCardsTable/PhysicalCardsTable'
import LayoutShell from 'src/components/LayoutShell/LayoutShell'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import { SubnavDesign } from 'src/components/Subnav/Subnav'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { OrganizationRoles } from 'src/enums'
import { useFlyoutDispatch } from 'src/hooks'
import { useExtendLocation } from 'src/hooks/locationHooks'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { AppLocation } from 'src/navigation/appLocations'
import { Rdrct } from 'src/navigation/Lnk'
import { myWalletRouteTree } from 'src/navigation/myWalletRouteTree'
import {
  createBudgetUrl,
  createVirtualCardUrl,
  homeUrl,
  myWalletBudgetsPendingURL,
  myWalletBudgetsURL,
  myWalletPhysicalCardsUrl,
  myWalletTransactionsURL,
  myWalletUrl,
  myWalletVirtualCardsUrl,
  registerAccountUrl,
  requestBudgetUrl,
  requestVirtualCardUrl,
  submitExpensesUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import {
  createSetIsCreateExpenseReportPopupOpen,
  createSetIsUploadReceiptsAutoMatchPopupOpen,
} from 'src/redux/actions/app.actions'
import {
  selectCalculatedBackgroundLocation,
  selectMyWalletBudgetsMatch,
  selectMyWalletBudgetsPendingMatch,
  selectMyWalletPhysicalCardsMatch,
  selectMyWalletTransactionsMatch,
  selectMyWalletVirtualCardsMatch,
  selectSubmitExpensesRouteMatch,
} from 'src/redux/selectors/router.selectors'

/**
 * MyWalletFamilyPage — route-level page for the My Wallet family. Contextual-
 * subnav deviation documented in src/page-components/AGENTS.md.
 */
const MyWalletFamilyPage: React.FC = () => {
  const history = useHistory()
  const { location: appLocation } = useExtendLocation()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )

  const dispatch = useFlyoutDispatch()

  const translate = useTranslate()

  const myWalletVirtualCardsMatch = useSelector(selectMyWalletVirtualCardsMatch)
  const myWalletPhysicalCardsMatch = useSelector(
    selectMyWalletPhysicalCardsMatch,
  )
  const myWalletTransactionsMatch = useSelector(selectMyWalletTransactionsMatch)
  const myWalletBudgetsMatch = useSelector(selectMyWalletBudgetsMatch)
  const myWalletBudgetsPendingMatch = useSelector(
    selectMyWalletBudgetsPendingMatch,
  )
  const myWalletReimbursementsMatch = useSelector(
    selectSubmitExpensesRouteMatch,
  )

  // CreditCardsSourceBudgetsEnabledCanIssueBudgetsCount was read here but
  // never dispatched by this file (the legacy effect fetched the different
  // SourceBudgetsEnabledCount key and two prefetch-only keys for child
  // routes); the page now fetches exactly what it reads.
  const { results: statisticsResults } = useQueryStatistics({
    statisticsKeys: [
      StatisticsKeys.CreditCardsSourceBudgetsEnabledCanIssueBudgetsCount,
      StatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCount,
      StatisticsKeys.CreditCardsCanIssueVirtualCardsCount,
    ] as const,
  })
  const creditCardsSourceBudgetsEnabledCanIssueBudgetsCount =
    statisticsResults[
      StatisticsKeys.CreditCardsSourceBudgetsEnabledCanIssueBudgetsCount
    ].data ?? 0
  const creditCardsActiveCanIssueVirtualCardsCount =
    statisticsResults[StatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCount]
      .data ?? 0
  const creditCardsCanIssueVirtualCardsCount =
    statisticsResults[StatisticsKeys.CreditCardsCanIssueVirtualCardsCount]
      .data ?? 0

  const canIssueBudget = creditCardsSourceBudgetsEnabledCanIssueBudgetsCount > 0

  const canCreateVirtualCards =
    creditCardsCanIssueVirtualCardsCount > 0 &&
    creditCardsActiveCanIssueVirtualCardsCount > 0

  const user = useCurrentUser()
  const isGuestRole = user?.organizationRole === OrganizationRoles.guest
  const canRequestVirtualCard = !isGuestRole

  const { REGISTER_ACCOUNT: canRegisterAccount } =
    useQueryUserFeatures([UserFeature.REGISTER_ACCOUNT]).data || {}
  const { VIEW_VIRTUAL_CARD_FEATURES: hasVirtualCardFeatures } =
    useQueryUserFeatures([UserFeature.VIEW_VIRTUAL_CARD_FEATURES]).data || {}

  // REDIRECT EFFECT
  // If the user does not have a particular flag, redirect them to the page they
  // are authorized to see
  // If they can't see the physical or virtual card pages, replace with home screen
  // These redirect effects only work for virtualcard and physicalcard pages
  useEffect((): void => {
    // hasVirtualCardFeatures = FALSE
    // canRegisterAccount = TRUE
    // redirect to Physical Cards URL
    if (
      typeof hasVirtualCardFeatures === 'boolean' &&
      !hasVirtualCardFeatures &&
      typeof canRegisterAccount === 'boolean' &&
      canRegisterAccount &&
      (appLocation === AppLocation.MyWalletVirtualCards ||
        appLocation === AppLocation.MyWalletVirtualCardDetail)
    ) {
      history.replace(myWalletPhysicalCardsUrl())
      return
    }

    // hasVirtualCardFeatures = TRUE
    // canRegisterAccount = FALSE
    // redirect to Virtual Cards URL
    if (
      typeof canRegisterAccount === 'boolean' &&
      !canRegisterAccount &&
      typeof hasVirtualCardFeatures === 'boolean' &&
      hasVirtualCardFeatures &&
      appLocation === AppLocation.MyWalletPhysicalCards
    ) {
      history.replace(myWalletVirtualCardsUrl())
      return
    }

    // hasVirtualCardFeatures = FALSE
    // canRegisterAccount = FALSE
    // redirect to Home page
    if (
      typeof canRegisterAccount === 'boolean' &&
      !canRegisterAccount &&
      typeof hasVirtualCardFeatures === 'boolean' &&
      !hasVirtualCardFeatures &&
      (appLocation === AppLocation.MyWalletVirtualCards ||
        appLocation === AppLocation.MyWalletVirtualCardDetail ||
        appLocation === AppLocation.MyWalletPhysicalCards)
    ) {
      history.replace(homeUrl())
    }
  }, [hasVirtualCardFeatures, canRegisterAccount, history])

  const dispatchUploadReceiptsAutoMatchPopup = (): void => {
    dispatch(createSetIsUploadReceiptsAutoMatchPopupOpen(true))
  }

  const dispatchCreateExpenseReportPopup = (): void => {
    dispatch(createSetIsCreateExpenseReportPopupOpen(true))
  }

  let headerText: string
  if (myWalletVirtualCardsMatch || myWalletPhysicalCardsMatch) {
    headerText = 'Cards'
  } else if (myWalletTransactionsMatch) {
    headerText = 'Transactions'
  } else if (myWalletReimbursementsMatch) {
    headerText = 'Reimbursements'
  } else if (myWalletBudgetsMatch || myWalletBudgetsPendingMatch) {
    headerText = 'Budgets'
  } else {
    headerText = 'Not Found'
  }

  const myWalletPhysicalCardsButtons = [
    {
      design: ButtonDesigns.designCtaInverse,
      type: ButtonTypes.button,
      text: translate('Enroll Card'),
      linkProps: {
        to: registerAccountUrl(),
        ariaLabel: translate('Enroll Card'),
        testId: 'enroll-card-btn',
      },
    },
  ]

  const myWalletVirtualCardsButtons = [
    ...(canRequestVirtualCard
      ? [
          {
            design: ButtonDesigns.designCtaInverse,
            type: ButtonTypes.button,
            text: translate('Request Card'),
            linkProps: {
              to: requestVirtualCardUrl(),
              ariaLabel: translate('Request Card'),
            },
          },
        ]
      : []),
    ...(canCreateVirtualCards
      ? [
          {
            design: ButtonDesigns.designCta,
            type: ButtonTypes.button,
            text: translate('Create Card'),
            linkProps: {
              to: createVirtualCardUrl(),
              ariaLabel: translate('Create Card'),
            },
          },
        ]
      : []),
  ]

  const myWalletBudgetsButtons = [
    {
      design: ButtonDesigns.designCtaInverse,
      type: ButtonTypes.button,
      text: translate('Request Budget'),
      linkProps: {
        to: requestBudgetUrl(),
        ariaLabel: translate('Request Budget'),
      },
    },
    ...(canIssueBudget
      ? [
          {
            design: ButtonDesigns.designCta,
            type: ButtonTypes.button,
            text: translate('Create Budget'),
            linkProps: {
              to: createBudgetUrl(),
              ariaLabel: translate('Create Budget'),
            },
          },
        ]
      : []),
  ]

  const myWalletTransactionsButtons = [
    {
      design: ButtonDesigns.designCta,
      type: ButtonTypes.button,
      text: translate('Auto-Match Receipts'),
      onClick: dispatchUploadReceiptsAutoMatchPopup,
    },
  ]

  const myWalletReimbursementsButtons = [
    {
      design: ButtonDesigns.designCta,
      type: ButtonTypes.button,
      text: translate('Create Report'),
      onClick: dispatchCreateExpenseReportPopup,
    },
  ]

  // TODO: Restrict buttons by permissions / features

  /* @ts-ignore-next-line */
  let buttons = []
  if (myWalletPhysicalCardsMatch) {
    buttons = myWalletPhysicalCardsButtons
  } else if (myWalletVirtualCardsMatch) {
    buttons = myWalletVirtualCardsButtons
  } else if (myWalletBudgetsMatch || myWalletBudgetsPendingMatch) {
    buttons = myWalletBudgetsButtons
  } else if (myWalletTransactionsMatch) {
    buttons = myWalletTransactionsButtons
  } else if (myWalletReimbursementsMatch) {
    buttons = myWalletReimbursementsButtons
  } else {
    buttons = []
  }

  const subnavOptions = useMemo(() => {
    if (myWalletVirtualCardsMatch || myWalletPhysicalCardsMatch) {
      const baseOptions = []
      if (hasVirtualCardFeatures) {
        baseOptions.push({
          label: translate('Virtual Cards'),
          to: myWalletVirtualCardsUrl(),
          isActive: myWalletVirtualCardsMatch !== null,
          ariaLabelSource: translate('Virtual Cards'),
          'data-testid': 'MyWalletVirtualCards',
        })
      }

      return canRegisterAccount
        ? [
            {
              label: translate('Physical Cards'),
              to: myWalletPhysicalCardsUrl(),
              isActive: myWalletPhysicalCardsMatch !== null,
              ariaLabelSource: translate('Physical Cards'),
              'data-testid': 'MyWalletPhysicalCards',
            },
            ...baseOptions,
          ]
        : [...baseOptions]
    } else if (myWalletBudgetsMatch || myWalletBudgetsPendingMatch) {
      return [
        {
          label: translate('Overview'),
          to: myWalletBudgetsURL(),
          isActive: myWalletBudgetsMatch !== null,
          ariaLabelSource: translate('Overview'),
          'data-testid': 'MyWalletBudgets',
        },
        {
          label: translate('Pending Budgets'),
          to: myWalletBudgetsPendingURL(),
          isActive: myWalletBudgetsPendingMatch !== null,
          ariaLabelSource: translate('Pending Budgets'),
          'data-testid': 'MyWalletBudgetsPending',
        },
      ]
    } else {
      return []
    }
  }, [
    myWalletVirtualCardsMatch,
    myWalletPhysicalCardsMatch,
    myWalletTransactionsMatch,
    myWalletBudgetsMatch,
    myWalletBudgetsPendingMatch,
    myWalletReimbursementsMatch,
    canRegisterAccount,
    hasVirtualCardFeatures,
  ])

  return (
    <LayoutShell
      subnavDesign={SubnavDesign.Floating}
      subnavOptions={subnavOptions}
      content={
        <Switch location={calculatedBackgroundLocation}>
          <Route exact path={myWalletVirtualCardsUrl()}>
            <MyWalletVirtualCards />
          </Route>
          <Route exact path={myWalletPhysicalCardsUrl()}>
            <PhysicalCardsTable />
          </Route>
          <Route exact path={myWalletTransactionsURL()}>
            <ExpensesTransactions myTransactions />
          </Route>
          <Route exact path={myWalletBudgetsURL()}>
            <BudgetsAllBudgets />
          </Route>
          <Route exact path={myWalletBudgetsPendingURL()}>
            <BudgetsRequests />
          </Route>
          <Route exact path={submitExpensesUrl()}>
            <ApproverExpenseReports yourExpenses />
          </Route>
          <Route exact path={myWalletUrl()}>
            <Rdrct to={myWalletRouteTree.family.redirectTo} />
          </Route>
          <Route path={'/'}>
            <NotFoundBackgroundPageContent />
          </Route>
        </Switch>
      }
      title={
        <span data-testid="myWalletFamilyHeading">{translate(headerText)}</span>
      }
      /* @ts-ignore next-line */
      buttons={buttons}
    />
  )
}

export default MyWalletFamilyPage
